import { ChevronDown, LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useLivePoll } from "../lib/useLivePoll";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function AppHeader({ groups, activeGroupId, onSelectGroup }) {
  const { user, logout } = useAuth();
  const activeGroup = groups.find((g) => g.id === activeGroupId);
  const showSwitcher = groups.length > 1 || user?.role === "owner";
  const [hasLive, setHasLive] = useState(false);

  const checkLive = async () => {
    if (!activeGroupId) { setHasLive(false); return; }
    try {
      const { data } = await api.get(`/groups/${activeGroupId}/live-status`);
      setHasLive(!!data.has_live);
    } catch (_) { setHasLive(false); }
  };

  useEffect(() => { checkLive(); /* eslint-disable-next-line */ }, [activeGroupId]);
  useLivePoll(checkLive, 15000, [activeGroupId], { immediate: false });

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 h-16 text-white shadow-md"
      style={{
        background: "linear-gradient(90deg, #1864ab 0%, #1971c2 100%)",
      }}
      data-testid="app-header"
    >
      <div className="max-w-[480px] mx-auto h-full px-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-2xl leading-none" aria-hidden>🐎</span>
          <div className="min-w-0">
            <div className="font-display font-black text-lg leading-tight truncate flex items-center gap-1.5" data-testid="app-title">
              HorseTips4fun
              {hasLive && (
                <span
                  data-testid="header-live-badge"
                  aria-live="polite"
                  className="animate-pulse inline-flex items-center gap-1 bg-[#c92a2a] text-white text-[9px] font-black uppercase tracking-widest rounded-full px-1.5 py-0.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE
                </span>
              )}
            </div>
            {activeGroup && (
              <div className="text-[11px] font-semibold uppercase tracking-wider opacity-80 truncate">
                {activeGroup.name}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {showSwitcher && groups.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="bg-white/15 hover:bg-white/25 active:scale-95 transition-transform rounded-full px-3 py-1.5 flex items-center gap-1.5 text-sm font-semibold max-w-[160px]"
                  data-testid="group-switcher-trigger"
                >
                  <span className="truncate">{activeGroup ? activeGroup.name : "Select group"}</span>
                  <ChevronDown className="w-4 h-4 shrink-0" strokeWidth={2.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[220px]" data-testid="group-switcher-menu">
                <DropdownMenuLabel>Your groups</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {groups.map((g) => (
                  <DropdownMenuItem
                    key={g.id}
                    onSelect={() => onSelectGroup(g.id)}
                    data-testid={`group-switcher-item-${g.invite_code}`}
                    className="font-semibold"
                  >
                    <span className="mr-2">🏇</span>
                    <span className="flex-1 truncate">{g.name}</span>
                    <span className="text-[10px] font-bold uppercase text-[#6c757d]">{g.invite_code}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 active:scale-95 transition-transform flex items-center justify-center text-xl"
                aria-label="Account menu"
                data-testid="account-menu-trigger"
              >
                <span>{user?.avatar_emoji || "🐎"}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="account-menu">
              <DropdownMenuLabel className="font-display">
                {user?.name}
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#6c757d] mt-0.5">
                  {user?.role}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={logout} data-testid="logout-menu-item" className="font-semibold text-[#c92a2a]">
                <LogOut className="w-4 h-4 mr-2" strokeWidth={2.5} />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
